import { useEffect, useState } from 'react';
import { COOKIE_CONSENT_DECIDED_EVENT, isCookieConsentUndecided } from './CookieBanner.tsx';

const VISITED_KEY = 'fantasy-manager:pwa-visited';
const DISMISSED_KEY = 'fantasy-manager:pwa-install-dismissed';

/** How long a "not now" dismissal lasts before the prompt is eligible again
 * (T-110). FPL runs on a weekly gameweek cycle, so two gameweeks is long
 * enough that this never nags someone inside a single week's decisions, but
 * short enough that one reflexive tap doesn't cost them the home-screen
 * path — and with it, iOS push in phase 2 — for good. Declining or
 * accepting Chrome's own install dialog is exempt from this: see `install`
 * below. */
const DISMISS_INTERVAL_MS = 14 * 24 * 60 * 60 * 1000;

/** Chrome/Android's install trigger — not in TypeScript's DOM lib. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

/** `'not-now'` is our own ghost-button dismissal (or "Got it" on the iOS
 * instructions) and expires after `DISMISS_INTERVAL_MS`. `'native-dialog'`
 * is Chrome's own install dialog resolving, accepted or declined either way
 * — that's a considered answer to "do you want this installed?", not a
 * reflex, so it's permanent and never expires. */
type DismissalOutcome = 'not-now' | 'native-dialog';

interface DismissalRecord {
  outcome: DismissalOutcome;
  dismissedAt: number;
}

function isDismissalRecord(value: unknown): value is DismissalRecord {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  return (
    (record.outcome === 'not-now' || record.outcome === 'native-dialog') &&
    typeof record.dismissedAt === 'number'
  );
}

/** Reads the stored dismissal, upgrading the pre-T-110 format (a bare
 * `'true'`, permanent, no timestamp) in place. That old value carried no
 * outcome and no date, so it's treated as a fresh `'not-now'` as of *now* —
 * not re-shown the moment this deploys, but not stuck permanently dismissed
 * either; it re-enters the normal interval from here. */
function readDismissal(): DismissalRecord | null {
  const raw = localStorage.getItem(DISMISSED_KEY);
  if (raw === null) return null;
  if (raw === 'true') {
    const upgraded: DismissalRecord = { outcome: 'not-now', dismissedAt: Date.now() };
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(upgraded));
    return upgraded;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return isDismissalRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function isDismissalActive(record: DismissalRecord | null): boolean {
  if (!record) return false;
  if (record.outcome === 'native-dialog') return true;
  return Date.now() - record.dismissedAt < DISMISS_INTERVAL_MS;
}

function storeDismissal(outcome: DismissalOutcome): DismissalRecord {
  const record: DismissalRecord = { outcome, dismissedAt: Date.now() };
  localStorage.setItem(DISMISSED_KEY, JSON.stringify(record));
  return record;
}

function wasVisitedBefore(): boolean {
  return localStorage.getItem(VISITED_KEY) === 'true';
}

function isRunningStandalone(): boolean {
  // iOS has no `display-mode` media feature to speak of but exposes this
  // instead; every other install-capable browser goes through matchMedia.
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return iosStandalone || window.matchMedia('(display-mode: standalone)').matches;
}

function isIOSDevice(): boolean {
  const ua = window.navigator.userAgent;
  // iPadOS 13+ reports as "Macintosh" in its user agent string like a real
  // Mac — multi-touch is the only reliable way left to tell it apart from
  // one, so this checks for that rather than assuming iPhone/iPod covers it.
  const isIPad = /ipad/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  return /iphone|ipod/i.test(ua) || isIPad;
}

/**
 * The install prompt for the home-screen shell (T-109).
 *
 * Trigger: the *second* visit, not the first. `beforeinstallprompt` fires
 * plenty of browsers on a first load too, but showing anything install-
 * shaped the moment someone arrives is exactly how permission-style prompts
 * get dismissed on reflex before the app has proven it's worth keeping.
 * Having loaded the app on a prior occasion is direct evidence someone
 * came back on their own, which is a real signal rather than a guess at
 * engagement (time-on-page, scroll depth) that a single confused session
 * can fake. `wasVisitedBefore` is read once, before this visit marks
 * itself, so "returning" always means "as of a previous visit".
 *
 * Chrome/Android supplies `beforeinstallprompt`, an event with no iOS
 * equivalent — Safari never fires it and has no scriptable install action
 * at all, so on a detected iOS device this instead shows the manual steps
 * (Share, then Add to Home Screen). Nothing renders on a platform that is
 * neither: there's no honest CTA to give a desktop browser with no
 * install capability offered.
 *
 * Two bars, one bottom of the screen (T-110): `CookieBanner` and this both
 * render a fixed bottom bar, and both can be eligible on the same visit —
 * someone who ignored the cookie banner once and came back is exactly the
 * "returning visitor" this prompt is waiting for. Consent wins the slot
 * outright rather than the two stacking: it's a live legal/trust question
 * (what gets tracked, right now) where install is a pure convenience offer
 * that's fine to defer, and stacking two unrelated bottom bars on a first
 * screen reads as noisier, not more considered. So this renders nothing
 * while `CookieBanner` still has an undecided consent to show for —
 * `isCookieConsentUndecided`/`COOKIE_CONSENT_DECIDED_EVENT` are how it
 * knows, since neither component is the other's parent (see main.tsx/App.tsx).
 */
export function InstallPrompt() {
  const [isReturningVisitor] = useState(wasVisitedBefore);
  const [dismissal, setDismissal] = useState(readDismissal);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isStandalone] = useState(isRunningStandalone);
  const [isCookieConsentPending, setIsCookieConsentPending] = useState(isCookieConsentUndecided);

  useEffect(() => {
    localStorage.setItem(VISITED_KEY, 'true');
  }, []);

  useEffect(() => {
    function handleBeforeInstallPrompt(event: Event) {
      event.preventDefault(); // suppress the browser's own mini-infobar; this UI replaces it
      setDeferredPrompt(event as BeforeInstallPromptEvent);
    }
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  useEffect(() => {
    function handleConsentDecided() {
      setIsCookieConsentPending(false);
    }
    window.addEventListener(COOKIE_CONSENT_DECIDED_EVENT, handleConsentDecided);
    return () => window.removeEventListener(COOKIE_CONSENT_DECIDED_EVENT, handleConsentDecided);
  }, []);

  function dismiss() {
    setDismissal(storeDismissal('not-now'));
  }

  async function install() {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    await deferredPrompt.userChoice; // the event is single-use either way
    setDismissal(storeDismissal('native-dialog')); // accepted or declined via Chrome's own dialog — don't ask again
  }

  const isIOS = isIOSDevice();
  if (
    isStandalone ||
    isCookieConsentPending ||
    isDismissalActive(dismissal) ||
    !isReturningVisitor ||
    !(deferredPrompt || isIOS)
  ) {
    return null;
  }

  return (
    // Reuses .cookie-banner's fixed-bottom-bar styling — it's already
    // exactly this shape: a non-modal bar pinned to the bottom, not specific
    // to what it's announcing. Safe to share now that the two never render
    // at once (see the doc comment above).
    <div className="cookie-banner rise-in" role="dialog" aria-labelledby="install-prompt-heading">
      <div className="cookie-banner-body">
        <p id="install-prompt-heading" className="cookie-banner-heading">
          Install The Gaffer
        </p>
        <p className="hint">
          {deferredPrompt
            ? 'Add it to your home screen for one-tap access — no app store, no download.'
            : 'Add it to your home screen: tap Share, then "Add to Home Screen".'}
        </p>
      </div>
      <div className="row row--flush cookie-banner-actions">
        {deferredPrompt && (
          <button type="button" onClick={install}>
            Install
          </button>
        )}
        <button type="button" className="ghost" onClick={dismiss}>
          {deferredPrompt ? 'Not now' : 'Got it'}
        </button>
      </div>
    </div>
  );
}
